import { faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FilterConfig, FilterOption } from '@supply-mover-2-0/core';
import React, { useEffect, useRef, useState } from 'react';
import { textSearchPendingService } from '../../list-builder/services/TextSearchPendingService';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Text } from '../base/Text';

interface BaseTextInputProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: FilterOption[];
	onValuesChange: (values: FilterOption[]) => void;
	disabled?: boolean;
	isModalOpen?: boolean;
	lockedValues?: FilterOption[];
	debounceMs?: number;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	layout?: 'sidebar' | 'tray' | 'inline' | 'bar';
}

/**
 * BaseTextInput Component
 *
 * A filter component that provides text input with debouncing, search icon,
 * and clear functionality.
 */
export const BaseTextInput: React.FC<BaseTextInputProps> = ({
	filterType,
	config,
	selectedValues,
	onValuesChange,
	disabled = false,
	isModalOpen = false,
	lockedValues = [],
	debounceMs = 300,
	size = 'md',
	layout: _layout = 'inline',
}) => {
	const colors = useThemeColors();

	// Size-based styling (matching Input component)
	const sizeClasses = {
		xs: 'py-2 text-xs',
		sm: 'py-1.5 text-sm',
		// h-[--control-h] rather than padding alone, so the search box is
		// exactly as tall as the selects and the Filters button beside it.
		md: 'py-2 text-sm h-[var(--control-h,2.25rem)]',
		lg: 'py-2.5 text-base',
	}[size];

	// Label size classes (matching FormField)
	const labelSizeClass = {
		xs: 'text-xs',
		sm: 'text-sm',
		md: 'text-sm',
		lg: 'text-base',
	}[size];

	const [inputValue, setInputValue] = useState('');
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	// Tracks the last value WE emitted via onValuesChange so the sync effect below
	// can tell our own committed value (an echo) apart from a genuine external
	// change (programmatic clear, a loaded saved search). Without this guard,
	// committing the debounced value mid-typing re-syncs inputValue back to the
	// just-committed value and silently drops any keystrokes typed during the
	// commit → re-render window — the "missed keystrokes" bug.
	const lastEmittedRef = useRef<string | null>(null);

	// Check if value is locked
	const currentValue = selectedValues[0] || lockedValues[0];
	const isLocked = lockedValues.length > 0;

	// Lists this filter feeds — where the pending-search flag gets raised while
	// an edit is debouncing (PD-14404).
	const filterListKeys: Array<string> = config.listKeys ?? [];

	// Sync the input from an EXTERNAL filter-value change only. Skip when the
	// incoming value is just an echo of what we last emitted, or when the user is
	// actively editing (input focused) — either case would otherwise clobber
	// in-progress typing.
	useEffect(() => {
		const label = currentValue?.label ?? '';

		if (label === lastEmittedRef.current) {
			return;
		}

		if (
			inputRef.current != null &&
			document.activeElement === inputRef.current
		) {
			return;
		}

		setInputValue(label);
	}, [currentValue]);

	// Debounced change handler
	const handleChange = (value: string) => {
		setInputValue(value);

		// Announce the un-committed edit immediately so list surfaces show
		// loading feedback instead of the previous search's stale empty state
		// while the debounce runs (PD-14404).
		textSearchPendingService.setPending(filterListKeys, filterType, true);

		// Clear existing timer
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		// Set new timer
		debounceTimerRef.current = setTimeout(() => {
			textSearchPendingService.setPending(filterListKeys, filterType, false);

			if (value.trim()) {
				// Record what we're emitting so the sync effect treats the echoed
				// committed value as our own change, not an external one.
				lastEmittedRef.current = value;
				// Create filter option with the text value
				onValuesChange([
					{
						id: value,
						label: value,
					},
				]);
			} else {
				// Clear filter when text is empty
				lastEmittedRef.current = '';
				onValuesChange([]);
			}
		}, debounceMs);
	};

	// Clear handler
	const handleClear = () => {
		setInputValue('');
		lastEmittedRef.current = '';
		textSearchPendingService.setPending(filterListKeys, filterType, false);
		onValuesChange([]);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
			debounceTimerRef.current = null;
		}
		// Focus the input after clearing
		inputRef.current?.focus();
	};

	// Cleanup timer (and any still-pending announcement) on unmount
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			textSearchPendingService.setPending(filterListKeys, filterType, false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- unmount-only cleanup; filterType/config are stable for a mounted filter
	}, []);

	// Auto-select default value if nothing is selected
	useEffect(() => {
		if (!currentValue && config.defaultValue) {
			onValuesChange([config.defaultValue]);
		}
	}, []);

	return (
		<div className="mb-4">
			{config.label && (
				<Text
					as="label"
					className={cn(
						'block font-medium mb-1',
						// Every filter shows its label above the field — bar and
						// tray alike (owner decisions 2026-08-03), keyword search
						// included.
						cn(labelSizeClass, colors.classes.textPrimary),
					)}
				>
					{config.label}
					{config.required && (
						<Text as="span" className={cn(colors.classes.textDanger, 'ml-1')}>
							*
						</Text>
					)}
				</Text>
			)}

			<div className="relative">
				{/* Search Icon */}
				<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
					<FontAwesomeIcon
						icon={faSearch}
						className={colors.classes.textSecondary}
						style={{ fontSize: '14px' }}
					/>
				</div>

				{/* Input Field */}
				<input
					ref={inputRef}
					type="text"
					value={inputValue}
					onChange={(e) => handleChange(e.target.value)}
					placeholder={config.placeholder || 'Search...'}
					disabled={disabled || isModalOpen || isLocked}
					className={cn(
						'w-full pl-9 pr-10 border rounded-md',
						'has-icon-left',
						sizeClasses,
						colors.classes.focusRingPrimary,
						'transition-colors duration-200',
						(disabled || isModalOpen || isLocked) &&
							'opacity-50 cursor-not-allowed',
						colors.classes.bgPrimary,
						colors.classes.borderDefault,
						colors.classes.textPrimary,
					)}
				/>

				{/* Clear Button */}
				{inputValue && !isLocked && (
					<button
						type="button"
						onClick={handleClear}
						disabled={disabled || isModalOpen}
						className={cn(
							'absolute inset-y-0 right-0 pr-3 flex items-center',
							disabled || isModalOpen ? 'cursor-not-allowed' : 'cursor-pointer',
						)}
					>
						<FontAwesomeIcon
							icon={faTimes}
							className={cn(
								colors.classes.textSecondary,
								'hover:' + colors.classes.textPrimary,
								'transition-colors',
							)}
							style={{ fontSize: '14px' }}
						/>
					</button>
				)}
			</div>

			{/* Locked indicator */}
			{isLocked && currentValue && (
				<div
					className={cn(
						'text-sm mt-2 flex items-center',
						colors.classes.textSecondary,
					)}
				>
					<FontAwesomeIcon
						icon={faSearch}
						className="mr-1"
						style={{ fontSize: '12px' }}
					/>
					<Text>Locked search: {currentValue.label}</Text>
				</div>
			)}
		</div>
	);
};
